/**
 * TanStack Query hooks for Data Table Management
 * Provides data table/column CRUD, scanning, and sensitive data detection
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { get, post } from '@/lib/api-client'
import { isApiError } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth-store'
import type {
  DataTable,
  DataColumn,
  DataTableListResponse,
  DataTableFilters,
  CreateDataTableRequest,
  AddColumnRequest,
  ScanResult,
  SensitiveDataReport,
  SampleValuesResponse,
  CatalogSummary,
} from '@/types/data-table'

/**
 * Query keys for data table management
 */
export const dataTableKeys = {
  all: ['dataTables'] as const,
  lists: () => [...dataTableKeys.all, 'list'] as const,
  list: (filters: DataTableFilters) =>
    [...dataTableKeys.lists(), filters] as const,
  details: () => [...dataTableKeys.all, 'detail'] as const,
  detail: (id: string) => [...dataTableKeys.details(), id] as const,
  columns: (tableId: string) =>
    [...dataTableKeys.all, 'columns', tableId] as const,
  sensitiveData: (tableId: string) =>
    [...dataTableKeys.all, 'sensitive-data', tableId] as const,
  samples: (columnId: string) =>
    [...dataTableKeys.all, 'samples', columnId] as const,
  catalogSummary: () => [...dataTableKeys.all, 'catalog-summary'] as const,
}

// ============================================================================
// QUERY HOOKS - Data Tables
// ============================================================================

/**
 * Fetch list of data tables with pagination and filters
 *
 * @param filters - Query parameters (page, pageSize, applicationId, classificationId, search)
 * @returns Query result with data tables array and pagination metadata
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useDataTables({
 *   applicationId: 'app-id-123',
 *   page: 1,
 *   pageSize: 20
 * })
 * ```
 */
export function useDataTables(filters: DataTableFilters = {}) {
  const { user } = useAuthStore()
  const tenantId = user?.tenantId ?? ''

  return useQuery({
    queryKey: dataTableKeys.list(filters),
    queryFn: async () => {
      const queryParams = new URLSearchParams()

      if (filters.page !== undefined) {
        queryParams.set('page', String(filters.page))
      }
      if (filters.pageSize !== undefined) {
        queryParams.set('pageSize', String(filters.pageSize))
      }
      if (filters.applicationId) {
        queryParams.set('applicationId', filters.applicationId)
      }
      if (filters.classificationId) {
        queryParams.set('classificationId', filters.classificationId)
      }
      if (filters.search) {
        queryParams.set('search', filters.search)
      }
      if (filters.requiresReview !== undefined) {
        queryParams.set('requiresReview', String(filters.requiresReview))
      }
      if (filters.minConfidence !== undefined) {
        queryParams.set('minConfidence', String(filters.minConfidence))
      }
      if (filters.hasRegulatoryFlags !== undefined) {
        queryParams.set('hasRegulatoryFlags', String(filters.hasRegulatoryFlags))
      }

      const queryString = queryParams.toString()
      const url = queryString ? `/data-tables?${queryString}` : '/data-tables'

      return get<DataTableListResponse>(url)
    },
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000,
    enabled: !!tenantId,
  })
}

/**
 * Fetch single data table by ID
 *
 * @param id - Data table ID
 * @returns Query result with table details
 *
 * @example
 * ```tsx
 * const { data: table } = useDataTable('table-id-123')
 * ```
 */
export function useDataTable(id: string) {
  return useQuery({
    queryKey: dataTableKeys.detail(id),
    queryFn: () => get<DataTable>(`/data-tables/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch columns for a data table
 *
 * @param tableId - Data table ID
 * @returns Query result with array of columns
 *
 * @example
 * ```tsx
 * const { data: columns } = useDataTableColumns('table-id-123')
 * ```
 */
export function useDataTableColumns(tableId: string) {
  return useQuery({
    queryKey: dataTableKeys.columns(tableId),
    queryFn: () => get<DataColumn[]>(`/data-tables/${tableId}/columns`),
    enabled: !!tableId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch sensitive data report for a table
 *
 * @param tableId - Data table ID
 * @returns Query result with sensitive data findings
 *
 * @example
 * ```tsx
 * const { data: report } = useSensitiveData('table-id-123')
 * ```
 */
export function useSensitiveData(tableId: string) {
  return useQuery({
    queryKey: dataTableKeys.sensitiveData(tableId),
    queryFn: () => get<SensitiveDataReport>(`/data-tables/${tableId}/sensitive-data`),
    enabled: !!tableId,
    // Fresh scan each time
    staleTime: 0,
  })
}

/**
 * Fetch sample values for a column
 *
 * @param columnId - Data column ID
 * @returns Query result with masked sample values
 *
 * @example
 * ```tsx
 * const { data: samples } = useSampleValues('column-id-123')
 * ```
 */
export function useSampleValues(columnId: string) {
  return useQuery({
    queryKey: dataTableKeys.samples(columnId),
    queryFn: () =>
      get<SampleValuesResponse>(`/data-tables/columns/${columnId}/samples`),
    enabled: !!columnId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch catalog summary (Application -> Schema -> Table -> Columns)
 *
 * @returns Query result with nested catalog structure
 */
export function useCatalogSummary() {
  const { user } = useAuthStore()
  const tenantId = user?.tenantId ?? ''

  return useQuery({
    queryKey: dataTableKeys.catalogSummary(),
    queryFn: () => get<CatalogSummary>('/data-tables/catalog-summary'),
    staleTime: 5 * 60 * 1000,
    enabled: !!tenantId,
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create new data table
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * const createTable = useCreateDataTable()
 * createTable.mutate({
 *   applicationId: 'app-id-123',
 *   schemaName: 'public',
 *   tableName: 'users',
 *   displayName: 'Users Table'
 * })
 * ```
 */
export function useCreateDataTable() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateDataTableRequest) =>
      post<DataTable>('/data-tables', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataTableKeys.lists() })
      toast.success('Data table created successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to create data table'
      toast.error(message)
    },
  })
}

/**
 * Add column to data table
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * const addColumn = useAddColumn()
 * addColumn.mutate({
 *   tableId: 'table-id-123',
 *   data: {
 *     columnName: 'email',
 *     displayName: 'Email Address',
 *     dataType: 'varchar'
 *   }
 * })
 * ```
 */
export function useAddColumn() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tableId,
      data,
    }: {
      tableId: string
      data: AddColumnRequest
    }) => post<DataColumn>(`/data-tables/${tableId}/columns`, data),
    onSuccess: (_, { tableId }) => {
      // Invalidate columns cache
      queryClient.invalidateQueries({
        queryKey: dataTableKeys.columns(tableId),
      })
      toast.success('Column added successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to add column'
      toast.error(message)
    },
  })
}

/**
 * Scan data table to update metadata
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * const scanTable = useScanTable()
 * scanTable.mutate('table-id-123')
 * ```
 */
export function useScanTable() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tableId: string) =>
      post<ScanResult>(`/data-tables/${tableId}/scan`, {}),
    onSuccess: (result, tableId) => {
      // Invalidate table detail to show updated scan time
      queryClient.invalidateQueries({
        queryKey: dataTableKeys.detail(tableId),
      })
      toast.success(
        `Scan completed: ${result.rowCount.toLocaleString()} rows, ${result.columnsScanned} columns`
      )
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to scan table'
      toast.error(message)
    },
  })
}

/**
 * Detect sensitive data in table
 *
 * @returns Mutation object with mutate function
 *
 * @example
 * ```tsx
 * const detectSensitive = useDetectSensitiveData()
 * detectSensitive.mutate('table-id-123')
 * ```
 */
export function useDetectSensitiveData() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tableId: string) =>
      get<SensitiveDataReport>(`/data-tables/${tableId}/sensitive-data`),
    onSuccess: (report, tableId) => {
      // Update cache
      queryClient.setQueryData(
        dataTableKeys.sensitiveData(tableId),
        report
      )
      toast.success(`Found ${report.totalFindings} potential sensitive data findings`)
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to detect sensitive data'
      toast.error(message)
    },
  })
}
